import { MathText } from '@/features/content/MathText';
import type { NormalizedContentUnit } from '@/features/content/useComponent';
import { getDisplayedQuestions } from '@/features/questions/displayedQuestions';
import type {
  NormalizedMultipleChoiceOption,
  NormalizedQuestionData,
  NormalizedQuestionSectionData,
} from '@/features/questions/questionNormalizer';

import { PrintContentUnits } from './printContentUnits';
import styles from './PrintQuestionRenderer.module.css';

interface PrintableOption {
  contentUnits?: NormalizedContentUnit[];
  id: string;
  marker: string;
  text?: string;
}

export interface PrintableQuestion {
  id: string;
  kind: NormalizedQuestionData['kind'];
  label: string;
  options?: PrintableOption[];
  parts?: PrintableQuestion[];
  promptUnits: NormalizedContentUnit[];
}

function getPrintableOptionMarker(index: number) {
  const alphabetIndex = index % 26;
  const suffix = index >= 26 ? String(Math.floor(index / 26) + 1) : '';

  return `${String.fromCharCode(65 + alphabetIndex)}${suffix}`;
}

function createPrintableOption(
  option: NormalizedMultipleChoiceOption,
): PrintableOption {
  return {
    contentUnits: option.contentUnits,
    id: option.id,
    marker: getPrintableOptionMarker(option.index),
    text:
      option.text ??
      (option.contentUnits && option.contentUnits.length > 0
        ? undefined
        : option.label),
  };
}

export function createPrintableQuestion(
  question: NormalizedQuestionData,
): PrintableQuestion {
  return {
    id: question.id,
    kind: question.kind,
    label: question.label,
    // Select-all carries options exactly as multiple choice does. Listing only
    // one of the two kinds printed a select-all question as a bare prompt with
    // nothing to choose from.
    options:
      question.kind === 'multipleChoice' || question.kind === 'selectAll'
        ? question.options.map(createPrintableOption)
        : undefined,
    parts:
      question.kind === 'multiPart'
        ? question.parts.map(createPrintableQuestion)
        : undefined,
    promptUnits: question.promptUnits,
  };
}

function PrintableQuestionBlock({
  includeVideoUrls,
  question,
}: {
  includeVideoUrls: boolean;
  question: PrintableQuestion;
}) {
  return (
    <article className={styles.question}>
      <h4 className={styles.questionTitle}>
        <MathText text={question.label} />
      </h4>

      <div className={styles.prompt}>
        <PrintContentUnits
          headingLevel={4}
          includeVideoUrls={includeVideoUrls}
          units={question.promptUnits}
        />
      </div>

      {question.options ? (
        <ol className={styles.options}>
          {question.options.map((option) => (
            <li className={styles.option} key={option.id}>
              <span aria-hidden="true" className={styles.optionMarker}>
                {option.marker}
              </span>
              <div>
                {option.contentUnits && option.contentUnits.length > 0 ? (
                  <PrintContentUnits
                    headingLevel={4}
                    includeVideoUrls={includeVideoUrls}
                    units={option.contentUnits}
                  />
                ) : (
                  <MathText text={option.text ?? ''} />
                )}
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      {question.kind === 'exactAnswer' || question.kind === 'numerical' ? (
        <div className={styles.responseLine}>Answer:</div>
      ) : null}

      {question.kind === 'aiUpload' ? (
        <p className={styles.uploadNote}>
          Upload response prompt. Submission details are omitted from PDF
          exports.
        </p>
      ) : null}

      {question.parts ? (
        <div className={styles.partList}>
          {question.parts.map((part) => (
            <div className={styles.part} key={part.id}>
              <PrintableQuestionBlock
                includeVideoUrls={includeVideoUrls}
                question={part}
              />
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export function PrintQuestionSections({
  includeVideoUrls,
  sections,
}: {
  includeVideoUrls: boolean;
  sections: NormalizedQuestionSectionData[];
}) {
  const sectionsWithQuestions = sections
    .map((section) => ({
      section,
      ...getDisplayedQuestions(section.questions, section.kind),
    }))
    .filter(({ assigned, practice }) => assigned.length > 0 || practice.length > 0);

  if (sectionsWithQuestions.length === 0) {
    return null;
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>Questions</h2>
      {sectionsWithQuestions.map(({ assigned, practice, section }) => (
        <div className={styles.sectionGroup} key={section.id}>
          <h3 className={styles.sectionGroupHeading}>{section.heading}</h3>
          {assigned.map((question) => (
            <PrintableQuestionBlock
              includeVideoUrls={includeVideoUrls}
              key={question.id}
              question={createPrintableQuestion(question)}
            />
          ))}
          {practice.length > 0 ? (
            <>
              <h3 className={styles.sectionGroupHeading}>Practice Questions</h3>
              {practice.map((question) => (
                <PrintableQuestionBlock
                  includeVideoUrls={includeVideoUrls}
                  key={question.id}
                  question={createPrintableQuestion(question)}
                />
              ))}
            </>
          ) : null}
        </div>
      ))}
    </section>
  );
}
